import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

import { getRoles } from '../../api/roles';

import { handleRequestRejection } from '../../utils/asyncThunkRequest';

import { initialRoles } from '../../components/Roles/constants/initialValues';

export const fetchRoles = createAsyncThunk(
  'fetchRoles',
  async (params, { signal, rejectWithValue }) => {
    const source = axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel();
    });
    try {
      const response = await getRoles({ cancelToken: source.token });
      const { data, status } = response;
      return { data, status };
    } catch (error) {
      return rejectWithValue(error.response.status);
    }
  },
);

const INITIAL_STATE = {
  roles: {
    byId: {
      // [channelCIDKey]: { Roles[] }
    },
    allIds: [],
  },
  channelRoles: {
    byId: {
      // [channelCIDKey]: { Roles[] }
    },
    allIds: [],
  },
  bulkAction: {
    name: null,
    selected: {
      // [bulkActionName]: { users: [], roleId: null }
    },
  },
  loading: 'idle',
  error: null,
  currentRequestId: undefined,
};

export const RolesSlice = createSlice({
  name: 'roles',
  initialState: INITIAL_STATE,
  reducers: {
    setRebuzzerBulkAction: (state, { payload }) => {
      const { name, id } = payload || {};

      if (!payload) {
        delete state.bulkAction.selected;
      }

      if (payload && !state.bulkAction.selected[name]) {
        state.bulkAction.selected = {
          ...state.bulkAction.selected,
          [name]: { users: [], roleId: id },
        };
      }

      state.bulkAction.name = name;
    },
    setChangeBoostSelectedUsers: (state, { payload }) => {
      const { name, userId } = payload;
      const hasUserInArray = state.bulkAction.selected[name].users.some(
        id => id === userId,
      );

      if (hasUserInArray) {
        state.bulkAction.selected[name].users = state.bulkAction.selected[
          name
        ].users.filter(id => userId !== id);
      } else {
        state.bulkAction.selected[name].users.push(userId);
      }
    },
    resetRolesSlice: () => INITIAL_STATE,
  },
  extraReducers: builder => {
    builder.addCase(fetchRoles.pending, (state, action) => {
      if (state.loading === 'idle') {
        state.loading = 'pending';
        state.currentRequestId = action.meta.requestId;
      }
    });
    builder.addCase(fetchRoles.fulfilled, (state, action) => {
      const { meta, payload } = action;
      if (
        state.loading === 'pending' &&
        state.currentRequestId === meta.requestId
      ) {
        const byId = {};
        const allIds = [];
        payload.data.forEach(role => {
          byId[role.id] = { ...role };
          allIds.push(role.id);
        });
        state.roles.byId = { ...byId };
        state.roles.allIds = [...allIds];
        state.loading = 'idle';
        state.error = null;
        state.currentRequestId = undefined;
      }
    });
    builder.addCase(fetchRoles.rejected, (state, action) => {
      handleRequestRejection(state, action);
    });
  },
});

export const selectorGetRoles = (state, roleId) =>
  state.roles.roles.byId[roleId] || initialRoles;
export const selectorGetChannelRoles = (state, itemCIDKey) =>
  state.roles.channelRoles.byId[itemCIDKey] || [];
export const selectorGetAllIdRoles = state => state.roles.roles.allIds || [];
export const selectorGetByIdRoles = state => state.roles.roles.byId || {};
export const selectorRolesRequest = state => ({
  loading: state.loading,
  error: state.error,
  currentRequestId: state.currentRequestId,
});
export const selectorGetRebuzzerBulkActionName = state =>
  state.roles.bulkAction.name;
export const selectorGetRebuzzerChangeBoostSelectedUsers = state =>
  state.roles.bulkAction.selected?.[state.roles.bulkAction.name]?.users || [];
export const selectorGetRebuzzerChangeBoostSelectedId = state =>
  state.roles.bulkAction.selected?.[state.roles.bulkAction.name]?.roleId || '';

export const {
  resetRolesSlice,
  setRebuzzerBulkAction,
  setChangeBoostSelectedUsers,
} = RolesSlice.actions;

export default RolesSlice.reducer;
